import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import axiosInstance from '../../api/axiosConfig';
import { validators, sanitizeInput } from '../../utils/validation';
import './FormStyles.css';
import receptionBg from '../../assets/images/backgrounds/reception-bg.png';

const GeneralVisitorForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Check if this is a public form (accessed from /visitor route)
    const isPublic = location.pathname.startsWith('/visitor/form');

    const [formData, setFormData] = useState({
        visitorName: '',
        email: '',
        contactNumber: '',
        company: '',
        personToMeet: '',
        purpose: '',
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const API_URL = process.env.REACT_APP_API_URL || '';

    const validateField = (name, value) => {
        let error = '';

        switch (name) {
            case 'visitorName':
                const nameResult = validators.name(value, 'Visitor name');
                error = nameResult.message;
                break;
            case 'email':
                if (value) {
                    const emailResult = validators.email(value);
                    error = emailResult.message;
                }
                break;
            case 'contactNumber':
                const phoneResult = validators.phone(value);
                error = phoneResult.message;
                break;
            case 'purpose':
                const purposeResult = validators.required(value, 'Purpose of visit');
                if (!purposeResult.valid) {
                    error = purposeResult.message;
                } else {
                    const minResult = validators.minLength(value, 3, 'Purpose');
                    error = minResult.message;
                }
                break;
            default:
                break;
        }

        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = sanitizeInput(value);
        
        setFormData({
            ...formData,
            [name]: sanitizedValue
        });

        // Validate on change if field was touched
        if (touched[name]) {
            const error = validateField(name, sanitizedValue);
            setErrors({
                ...errors,
                [name]: error
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched({
            ...touched,
            [name]: true
        });

        const error = validateField(name, value);
        setErrors({
            ...errors,
            [name]: error
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const newErrors = {};
        let hasErrors = false;

        Object.keys(formData).forEach(field => {
            const error = validateField(field, formData[field]);
            if (error) {
                newErrors[field] = error;
                hasErrors = true;
            }
        });

        setErrors(newErrors);
        setTouched({
            visitorName: true,
            email: true,
            contactNumber: true,
            purpose: true
        });

        if (hasErrors) {
            return;
        }

        setLoading(true);

        try {
            if (isPublic) {
                // Public form - use plain axios without auth
                await axios.post(
                    `${API_URL}/api/public/visitor-forms`,
                    {
                        formType: 'general',
                        formData,
                        notes: formData.notes
                    }
                );
            } else {
                // Authenticated form - use axiosInstance
                await axiosInstance.post(
                    '/api/forms',
                    {
                        formType: 'general',
                        formData,
                        notes: formData.notes
                    }
                );
            }

            setSuccess(true);
            setTimeout(() => {
                navigate(isPublic ? '/visitor' : '/purpose-selection');
            }, 2000);
        } catch (error) {
            alert('Error submitting form: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate(isPublic ? '/visitor' : '/purpose-selection');
    };

    return (
        <div className="form-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="form-wrapper card fade-in">
                <div className="form-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="form-header-text">
                        <h2>General Visitor Registration</h2>
                        <p>Please fill in the visitor details below</p>
                    </div>
                    <div className="form-header-actions">
                        {!isPublic && user && (
                            <div className="form-user-info">
                                <strong>{user?.fullName || user?.username}</strong>
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn-back-form"
                            onClick={handleBack}
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="alert alert-success">
                        ✅ Visitor registered successfully! Redirecting...
                    </div>
                )}

                <form onSubmit={handleSubmit} className="visitor-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="visitorName" className="form-label">Full Name *</label>
                            <input
                                type="text"
                                id="visitorName"
                                name="visitorName"
                                className={`form-input ${errors.visitorName && touched.visitorName ? 'error' : ''}`}
                                value={formData.visitorName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            {errors.visitorName && touched.visitorName && (
                                <span className="error-message">{errors.visitorName}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className={`form-input ${errors.email && touched.email ? 'error' : ''}`}
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.email && touched.email && (
                                <span className="error-message">{errors.email}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="contactNumber" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                id="contactNumber"
                                name="contactNumber"
                                className={`form-input ${errors.contactNumber && touched.contactNumber ? 'error' : ''}`}
                                value={formData.contactNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="e.g., 33445566 or +97433445566"
                                required
                            />
                            {errors.contactNumber && touched.contactNumber && (
                                <span className="error-message">{errors.contactNumber}</span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="company" className="form-label">Company/Organization</label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                className="form-input"
                                value={formData.company}
                                onChange={handleChange}
                                maxLength="100"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="personToMeet" className="form-label">Person to Meet</label>
                            <input
                                type="text"
                                id="personToMeet"
                                name="personToMeet"
                                className="form-input"
                                value={formData.personToMeet}
                                onChange={handleChange}
                                maxLength="100"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="purpose" className="form-label">Purpose of Visit *</label>
                            <input
                                type="text"
                                id="purpose"
                                name="purpose"
                                className={`form-input ${errors.purpose && touched.purpose ? 'error' : ''}`}
                                value={formData.purpose}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                            />
                            {errors.purpose && touched.purpose && (
                                <span className="error-message">{errors.purpose}</span>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes" className="form-label">Additional Notes</label>
                        <textarea
                            id="notes"
                            name="notes"
                            className="form-textarea"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                            maxLength="500"
                        ></textarea>
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleBack}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Register Visitor'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GeneralVisitorForm;
